"use client";

import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import type { z } from "zod/v4";
import { useTranslations } from "next-intl";
import { passwordMinLength } from "@repo/utils";

interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  schema: z.ZodType<string>;
  onPasswordChange?: (password: string, isValid: boolean) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showValidationFeedback?: boolean;
  showValidationPills?: boolean;
}

export function PasswordInput({
  className,
  schema,
  onPasswordChange,
  onChange,
  showValidationFeedback = false,
  showValidationPills = true,
  ...props
}: PasswordInputProps) {
  const t = useTranslations();

  const [showPassword, setShowPassword] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [isValid, setIsValid] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Define fixed requirements based on common password rules
  const requirements = [
    {
      id: "length",
      label: t("common.validation.password.characters", {
        count: passwordMinLength,
      }),
      validator: (val: string) => val.length >= passwordMinLength,
    },
    {
      id: "uppercase",
      label: t("common.validation.password.uppercase"),
      validator: (val: string) => /[A-Z]/.test(val),
    },
    {
      id: "number",
      label: t("common.validation.password.number"),
      validator: (val: string) => /[0-9]/.test(val),
    },
    {
      id: "special",
      label: t("common.validation.password.special"),
      validator: (val: string) => /[!@#$%^&*]/.test(val),
    },
  ];

  // Calculate how many requirements are met
  const metRequirements = requirements.filter((req) =>
    req.validator(password),
  ).length;

  // Calculate strength percentage based on met requirements
  const strengthPercentage =
    password.length === 0 ? 0 : (metRequirements / requirements.length) * 100;

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Validate with schema
    const result = schema.safeParse(newPassword);
    setIsValid(result.success);

    // Call parent onChange if provided
    if (onChange) {
      onChange(e);
    }

    // Call onPasswordChange callback if provided
    if (onPasswordChange) {
      onPasswordChange(newPassword, result.success);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getStrengthColor = () => {
    if (strengthPercentage <= 33) return "bg-red-500";
    if (strengthPercentage <= 66) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthLabel = () => {
    if (strengthPercentage === 0) return "";
    if (strengthPercentage <= 33)
      return t("common.validation.password.strengthLabel.weak");
    if (strengthPercentage <= 66)
      return t("common.validation.password.strengthLabel.fair");
    return t("common.validation.password.strengthLabel.strong");
  };

  // Focus the input when showValidationFeedback changes to true
  React.useEffect(() => {
    if (showValidationFeedback && !isValid && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showValidationFeedback, isValid]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          ref={inputRef}
          type={showPassword ? "text" : "password"}
          className={cn(
            "pr-10",
            showValidationFeedback && !isValid
              ? "border-red-500 focus-visible:ring-red-500"
              : "",
            className,
          )}
          value={password}
          onChange={handlePasswordChange}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
          onClick={togglePasswordVisibility}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>

      {showValidationPills && (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("common.validation.password.strengthLabel.default")}
              </span>
              <span className="text-xs font-medium">{getStrengthLabel()}</span>
            </div>
            <Progress
              aria-label={t("common.validation.password.strengthLabel.default")}
              value={strengthPercentage}
              className="h-1.5"
              indicatorClassName={getStrengthColor()}
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {requirements.map((requirement) => {
              const isMet = requirement.validator(password);
              return (
                <div
                  key={requirement.id}
                  className={cn(
                    "text-xs px-2 py-1 rounded-full transition-colors",
                    isMet
                      ? "bg-green-100 text-green-700"
                      : showValidationFeedback && !isMet
                        ? "bg-red-100 text-red-700"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {requirement.label}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
